import { useEffect, useRef, useState } from 'react';
import AuctionAccessLookupForm from './AuctionAccessLookupForm';
import { AccountAlreadyExists, AuctionAccessLookupError, AuctionAccessNotFound } from './Results';
import AuctionAccessVerified from '../Review';
import { AuctionAccessLookupLoading } from '../shared/AuctionAccessLookupLoading';
import { AUCTION_ACCESS_LOOKUP_STATE } from './lookupState';
import useAuctionAccessLookup from './useAuctionAccessLookup';

const AuctionAccessLookup = ({
	anonymousId,
	error,
	initialAccount,
	initialValues,
	isReviewSubmitting,
	onContinueManually,
	onLookupSuccess,
	onLookupStateChange,
	onReviewNext,
	signInUrl,
	signUpPolicies,
}) => {
	const [isSignupHydrated, setIsSignupHydrated] = useState(Boolean(initialAccount));
	const [signupHydrationFailed, setSignupHydrationFailed] = useState(false);
	const hasHydratedSignup = useRef(Boolean(initialAccount));
	const latestOnLookupSuccess = useRef(onLookupSuccess);
	const { account, lookupState, submittedAuctionAccessId, submittedPhotoIdLastFour, submitLookup, tryAgain } =
		useAuctionAccessLookup(anonymousId);
	const displayedAccount = initialAccount || account;
	const displayedLookupState = initialAccount ? AUCTION_ACCESS_LOOKUP_STATE.SUCCESS : lookupState;

	useEffect(() => {
		latestOnLookupSuccess.current = onLookupSuccess;
	}, [onLookupSuccess]);

	useEffect(() => {
		onLookupStateChange?.(displayedLookupState === AUCTION_ACCESS_LOOKUP_STATE.SUCCESS);
	}, [displayedLookupState, onLookupStateChange]);

	useEffect(() => {
		if (
			lookupState !== AUCTION_ACCESS_LOOKUP_STATE.SUCCESS ||
			!account ||
			!onLookupSuccess ||
			hasHydratedSignup.current
		) {
			return;
		}

		hasHydratedSignup.current = true;
		let isActive = true;

		const hydrateSignup = async () => {
			try {
				await latestOnLookupSuccess.current(account);
				if (isActive) setIsSignupHydrated(true);
			} catch {
				if (isActive) setSignupHydrationFailed(true);
			}
		};

		hydrateSignup();

		return () => {
			isActive = false;
			hasHydratedSignup.current = false;
		};
	}, [account, lookupState]);

	const handleTryAgain = () => {
		hasHydratedSignup.current = false;
		setIsSignupHydrated(false);
		setSignupHydrationFailed(false);
		tryAgain();
	};

	if (signupHydrationFailed) {
		return (
			<AuctionAccessLookupError
				isUnavailable={false}
				onContinueManually={onContinueManually}
				onTryAgain={handleTryAgain}
			/>
		);
	}

	if (
		displayedLookupState === AUCTION_ACCESS_LOOKUP_STATE.LOADING ||
		(displayedLookupState === AUCTION_ACCESS_LOOKUP_STATE.SUCCESS && onLookupSuccess && !isSignupHydrated)
	) {
		return <AuctionAccessLookupLoading />;
	}

	if (displayedLookupState === AUCTION_ACCESS_LOOKUP_STATE.SUCCESS) {
		return (
			<AuctionAccessVerified
				account={displayedAccount}
				error={error}
				initialValues={initialValues}
				isReviewSubmitting={isReviewSubmitting}
				onCancel={onContinueManually}
				onNext={onReviewNext}
				signUpPolicies={signUpPolicies}
			/>
		);
	}

	if (displayedLookupState === AUCTION_ACCESS_LOOKUP_STATE.ACCOUNT_EXISTS) {
		return <AccountAlreadyExists account={account} onContinueManually={onContinueManually} signInUrl={signInUrl} />;
	}

	if (displayedLookupState === AUCTION_ACCESS_LOOKUP_STATE.NOT_FOUND) {
		return (
			<AuctionAccessNotFound
				onContinueManually={onContinueManually}
				onTryAgain={handleTryAgain}
				submittedAuctionAccessId={submittedAuctionAccessId}
				submittedPhotoIdLastFour={submittedPhotoIdLastFour}
			/>
		);
	}

	if (displayedLookupState !== AUCTION_ACCESS_LOOKUP_STATE.ENTRY) {
		return (
			<AuctionAccessLookupError
				isUnavailable={displayedLookupState === AUCTION_ACCESS_LOOKUP_STATE.UNAVAILABLE}
				onContinueManually={onContinueManually}
				onTryAgain={handleTryAgain}
			/>
		);
	}

	return <AuctionAccessLookupForm onBack={onContinueManually} onSubmit={submitLookup} />;
};

export default AuctionAccessLookup;
